import React, { useEffect, useState } from 'react';
import { Box, Button, Grid, IconButton, Link as MuiLink, Tooltip, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { Link } from 'react-router-dom';
import EditIcon from '@mui/icons-material/Edit';
import EventNoteIcon from '@mui/icons-material/EventNote';
import TodayIcon from '@mui/icons-material/Today';
import DescriptionIcon from '@mui/icons-material/Description';
import VisibilityIcon from '@mui/icons-material/Visibility';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import InboxIcon from '@mui/icons-material/Inbox';
import { useSnackbar } from 'notistack';
import PageTitle from '../../components/PageTitle';
import Loader from '../../common/Loader';
import CopyIcon from '../../common/CopyIcon';
import { apiConstants, getGeneric, postGeneric } from '../../common/constants';
import dayjs from 'dayjs';
import KpiCard from './shared/KpiCard';
import FilterBar from './shared/FilterBar';
import PageBanner from './shared/PageBanner';
import ExpandableAppointmentsTable from './shared/ExpandableAppointmentsTable';
import { exportXlsx } from './shared/exportHelpers';
import { editorEmailIfDifferent, getMoveTag, summarizeChanges, summarizeChangesText } from './shared/editLog';
import EditDocLinks from './shared/EditDocLinks';
import { ABD_EDIT_REASON_CATEGORY, fmt, getDefaultDateRange } from './shared/constants';

const textSx = { fontSize: '12px', color: '#333', lineHeight: 1.5 };
const subSx = { fontSize: '11px', color: '#888', lineHeight: 1.5 };

// Preponed / Postponed / Same day tag colours (same palette family as the listing chips).
const MOVE_TONES = {
    green: { bg: '#e8f5e9', color: '#2e7d32' },
    amber: { bg: '#fff3e0', color: '#e65100' },
    grey: { bg: '#eceff1', color: '#546e7a' },
};

const fmtEditedOn = (v) => {
    if (!v) return '-';
    const d = dayjs(v);
    return d.format(d.year() === dayjs().year() ? 'D MMM · h:mm A' : 'D MMM YYYY · h:mm A');
};

const buildColumns = ({ onAwbClick }) => [
    {
        field: 'merchant', headerName: 'Merchant', width: '12%',
        renderCell: ({ row }) => <Typography sx={textSx}>{row.merchantName || '-'}</Typography>,
    },
    {
        field: 'awb', headerName: 'AWB / Ref', width: '17%',
        renderCell: ({ row }) => (
            <Box>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Tooltip title='Show all edits of this shipment'>
                        <MuiLink component='button' type='button' onClick={() => onAwbClick(row.awbNumber)}
                            sx={{ ...textSx, fontWeight: 700, textDecoration: 'none' }}>{row.awbNumber}</MuiLink>
                    </Tooltip>
                    <CopyIcon text={row.awbNumber} />
                </Box>
                {row.reference && <Typography sx={subSx}>Ref {row.reference}</Typography>}
                <Typography sx={subSx}>
                    Appt #{row.appointmentNumber}
                    {row.editIndex != null && row.editsOnAppointment ? ` · Edit ${row.editIndex + 1} of ${row.editsOnAppointment}` : ''}
                </Typography>
            </Box>
        ),
    },
    {
        field: 'edited', headerName: 'Edited By / On', width: '15%',
        renderCell: ({ row }) => (
            <Box>
                <Typography sx={{ ...textSx, wordBreak: 'break-word' }}>{row.editedBy || row.editedByEmail || '-'}</Typography>
                {editorEmailIfDifferent(row.editedBy, row.editedByEmail) && (
                    <Typography sx={{ ...subSx, wordBreak: 'break-word' }}>{row.editedByEmail}</Typography>
                )}
                <Typography sx={subSx}>{fmtEditedOn(row.editedAt)}</Typography>
            </Box>
        ),
    },
    {
        field: 'changes', headerName: 'What Changed', width: '36%',
        renderCell: ({ row }) => {
            const tag = getMoveTag(row.changes);
            const tone = tag ? MOVE_TONES[tag.tone] : null;
            return (
                <Box>
                    {tag && (
                        <Box component='span' sx={{
                            display: 'inline-block', mb: 0.5, px: 1, py: '1px', borderRadius: '10px',
                            fontSize: '10.5px', fontWeight: 700, bgcolor: tone.bg, color: tone.color,
                        }}>{tag.label}</Box>
                    )}
                    {summarizeChanges(row.changes).map((l) => (
                        <Box key={l.key} sx={{ display: 'flex', gap: 1 }}>
                            <Typography sx={{ ...subSx, minWidth: 64, fontWeight: 600 }}>{l.label}</Typography>
                            <Typography sx={{ ...textSx, fontSize: '11.5px', fontWeight: l.strong ? 700 : 400 }}>{l.text}</Typography>
                        </Box>
                    ))}
                    <EditDocLinks changes={row.changes} awbNumber={row.awbNumber}
                        historyIndex={row.historyIndex} editIndex={row.editIndex} />
                </Box>
            );
        },
    },
    {
        field: 'reason', headerName: 'Reason', width: '14%',
        renderCell: ({ row }) => (
            <Box>
                <Typography sx={{ ...textSx, fontWeight: 600 }}>{row.reasonLabel || row.reasonCode || '-'}</Typography>
                {row.reasonRemarks && <Typography sx={subSx}>"{row.reasonRemarks}"</Typography>}
            </Box>
        ),
    },
    {
        field: 'action', headerName: 'Actions', width: '6%',
        renderCell: ({ row }) => (
            <Tooltip title='Order Details'>
                <IconButton component={Link} to={`/order-detail/${row.awbNumber}`} size='small'
                    sx={{ width: 30, height: 30, borderRadius: '6px', bgcolor: '#e3f2fd', color: '#1565c0', '& svg': { fontSize: 16 } }}>
                    <VisibilityIcon />
                </IconButton>
            </Tooltip>
        ),
    },
];

// Request body for POST /order/appointment_delivery/edits.
const buildBody = (filters, page, size) => ({
    fromDate: fmt(filters.fromDate),
    toDate: fmt(filters.toDate),
    merchantOid: filters.merchant || null,
    awbNumber: filters.awb?.trim() || null,
    editedBy: filters.editedBy?.trim() || null,
    reasonCode: filters.reason || null,
    page,
    size,
});

// Edited Appointments: one row per appointment edit (an appointment edited 3 times shows 3 times).
export default function EditedAppointments() {
    const { enqueueSnackbar } = useSnackbar();
    const def = getDefaultDateRange();
    const [filters, setFilters] = useState({ fromDate: def.fromDate, toDate: def.toDate });
    const [page, setPage] = useState(0);
    const [sizePerPage, setSizePerPage] = useState(50);
    const [reload, setReload] = useState(0);
    const [data, setData] = useState({ rows: [], total: 0, summary: {} });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [exporting, setExporting] = useState(false);
    const [reasonOptions, setReasonOptions] = useState([]);
    // Remounts the filter bar so its fields show filters applied from the table (AWB click).
    const [filterBarKey, setFilterBarKey] = useState(0);

    const applyFilters = (f) => { setFilters(f); setPage(0); setReload((r) => r + 1); };
    const showShipment = (awbNumber) => {
        applyFilters({ ...filters, awb: awbNumber });
        setFilterBarKey((k) => k + 1);
    };
    const columns = buildColumns({ onAwbClick: showShipment });

    // All reasons, inactive too, so older edits stay filterable.
    useEffect(() => {
        getGeneric(apiConstants.REASON_MASTER, { category: ABD_EDIT_REASON_CATEGORY, activeOnly: false })
            .then((res) => setReasonOptions((Array.isArray(res?.data) ? res.data : []).map((r) => ({ value: r.code, label: r.label || r.code }))))
            .catch(() => setReasonOptions([]));
    }, []);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        setError('');
        postGeneric(apiConstants.APPOINTMENT_DELIVERY_EDITS, buildBody(filters, page, sizePerPage))
            .then((res) => {
                if (!cancelled) setData({ rows: res?.data?.rows || [], total: res?.data?.total || 0, summary: res?.data?.summary || {} });
            })
            .catch((e) => {
                if (!cancelled) {
                    setData({ rows: [], total: 0, summary: {} });
                    setError(e?.response?.data?.message || 'Failed to load edited appointments');
                }
            })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [filters, page, sizePerPage, reload]);

    // Excel: every edit matching the filters (server caps the row count), one row per edit.
    const exportExcel = async () => {
        setExporting(true);
        try {
            const res = await postGeneric(apiConstants.APPOINTMENT_DELIVERY_EDITS, buildBody(filters, 0, data.total || 1));
            const rows = (res?.data?.rows || []).map((r) => ({
                'Merchant': r.merchantName || '',
                'AWB': r.awbNumber,
                'Ref': r.reference || '',
                'Order ID': r.orderId || '',
                'Appointment No.': r.appointmentNumber,
                'Edit No.': r.editIndex != null && r.editsOnAppointment ? `${r.editIndex + 1} of ${r.editsOnAppointment}` : '',
                'Edited By': r.editedBy || '',
                'Edited By Email': r.editedByEmail || '',
                'Edited On': r.editedAt ? dayjs(r.editedAt).format('D MMM YYYY, h:mm A') : '',
                'Moved': getMoveTag(r.changes)?.label || '',
                'What Changed': summarizeChangesText(r.changes),
                'Reason': r.reasonLabel || r.reasonCode || '',
                'Reason Remarks': r.reasonRemarks || '',
            }));
            exportXlsx('ABD_Edited_Appointments', [{ name: 'Edited Appointments', rows }]);
            // The server caps export size; say so instead of silently dropping rows.
            if (rows.length < data.total) {
                enqueueSnackbar(`Exported the latest ${rows.length} of ${data.total} edits (export limit). Narrow the filters for the rest.`, { variant: 'warning' });
            } else {
                enqueueSnackbar('Export ready', { variant: 'success' });
            }
        } catch (e) {
            enqueueSnackbar(e?.response?.data?.message || 'Export failed', { variant: 'error' });
        }
        setExporting(false);
    };

    const s = data.summary || {};

    return (
        <>
            <PageTitle>Edited Appointments</PageTitle>
            {loading && <Loader />}

            <Grid container spacing={2} sx={{ mb: 2 }}>
                <Grid item xs={12} md={3}>
                    <KpiCard color='teal' label='Total Edits' value={s.totalEdits} loading={loading}
                        sub='Shipments registered in range' icon={<EditIcon />}
                        info='Every saved edit on ABD shipments whose AWB was registered in the range. An appointment edited 3 times counts 3.' />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='blue' label='Appointments Edited' value={s.appointmentsEdited} loading={loading}
                        sub='Distinct appointments' icon={<EventNoteIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='purple' label='Edits Today' value={s.editsToday} loading={loading}
                        sub='Within the current filters' icon={<TodayIcon />} />
                </Grid>
                <Grid item xs={12} md={3}>
                    <KpiCard color='amber' label='Documents Replaced' value={s.documentsReplaced} loading={loading}
                        sub='Invoice / E-Way Bill / PO Copy' icon={<DescriptionIcon />}
                        info='Replaced files are kept; the old version is recorded in the edit.' />
                </Grid>
            </Grid>

            <PageBanner variant='info' icon={<EditIcon />} title='Edits are not attempts'>
                An edit changes the appointment in place before its cut-off. It is not counted as an attempt or a miss
                and does not appear in the ABD / B2B MIS. A new date or slot moves the EDD to the new appointment.
            </PageBanner>

            <FilterBar
                key={filterBarKey}
                initial={filters}
                show={['merchant', 'awb', 'editedBy', 'reason', 'dateRange']}
                reasonOptions={reasonOptions}
                onApply={applyFilters}
                onReset={() => { setFilters({ fromDate: def.fromDate, toDate: def.toDate }); setPage(0); setReload((r) => r + 1); }}
            />

            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1.5 }}>
                <Typography variant='caption' sx={{ color: '#888' }}>Newest edit first · one row per edit · date range = AWB registered date</Typography>
                <LoadingButton
                    variant='outlined' size='small' loading={exporting} disabled={!data.total}
                    startIcon={<FileDownloadOutlinedIcon />} onClick={exportExcel} sx={{ ml: 'auto' }}
                >Export (Excel)</LoadingButton>
            </Box>

            {error ? (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2 }}>
                    <Typography color='error' sx={{ mb: 1 }}>{error}</Typography>
                    <Button size='small' onClick={() => setReload((r) => r + 1)}>Retry</Button>
                </Box>
            ) : !loading && data.total === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#fff', borderRadius: 2 }}>
                    <InboxIcon sx={{ fontSize: 28, color: '#bdbdbd' }} />
                    <Typography sx={{ color: '#666', mb: 1 }}>No appointment edits on shipments registered in this period for the selected filters.</Typography>
                    <Button size='small' component={Link} to='/appointments/all'>Go to All Appointments</Button>
                </Box>
            ) : (
                <ExpandableAppointmentsTable
                    columns={columns}
                    rows={data.rows}
                    page={page}
                    sizePerPage={sizePerPage}
                    setPage={setPage}
                    setSizePerPage={setSizePerPage}
                    rowCount={data.total}
                    getRowId={(row) => `${row.awbNumber}-${row.appointmentNumber}-${row.editedAt}`}
                />
            )}
        </>
    );
}
